import { Button, Form, Space } from "antd";
import type { ColumnType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { FormSelect } from "~components/Form/FormSelect";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import type { RoleStatuses } from "~enums/roleStatuses";
import type { Roles } from "~enums/roles";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey";
import ImageView from "../../ImageView/ImageView.tsx";
import styles from "../AddPersonRolesModal.module.css";
import type { AddOfficialsTableRow } from "../types/addOfficialsTableRow";

const AddButton = ({ disabled }: { disabled: boolean }) => {
  const form = Form.useFormInstance();
  return (
    <Button
      type="default"
      size="small"
      icon={<i className="fas fa-plus" />}
      onClick={form.submit}
      className={styles.addButton}
      disabled={disabled}
    />
  );
};

export const useAddPersonRolesModalColumns = (roles: Roles[], statuses: RoleStatuses[], isLoading: boolean) => {
  const { formatMessage } = useIntl();
  const { sport = "" } = useParams();

  const columns: ColumnType<AddOfficialsTableRow>[] = [
    {
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameFullLocal",
      sorter: true,
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      render: (value, row) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
    },
    {
      title: formatMessage({ id: "nationality" }),
      dataIndex: "nationality",
      width: 60,
    },
    {
      title: formatMessage({ id: "gender" }),
      dataIndex: "gender",
      width: 60,
    },
    {
      width: 270,
      title: formatMessage({ id: "role" }),
      onCell: () => ({
        isEditing: true,
        title: formatMessage({ id: "role" }),
      }),
      render: () => (
        <FormSelect
          disabled={isLoading}
          rules={[
            {
              required: true,
              message: "",
            },
          ]}
          name="role"
          selectProps={{
            className: styles.formRolesSelect,
            options: roles.map((role) => ({
              label: formatMessage({ id: `roles.${getRoleTranslationKey(role, sport)}` }),
              value: role,
            })),
          }}
        />
      ),
    },
    {
      width: 100,
      title: formatMessage({ id: "status" }),
      onCell: () => ({ isEditing: true, title: formatMessage({ id: "status" }) }),
      render: () => (
        <FormSelect
          disabled={isLoading}
          name="status"
          selectProps={{
            className: styles.formSelect,
            options: statuses.map((status) => ({
              label: formatMessage({ id: `roleStatus.${status}` }),
              value: status,
            })),
          }}
        />
      ),
    },
    {
      align: "right",
      render: () => <AddButton disabled={isLoading} />,
    },
  ];

  return columns;
};
